import { Ref, computed, ref, watch } from "vue";
import { PlayerDetailModel } from "@/api-connector/play-digital-api/models/account-models";
import { AccountNotificationType } from "./models/authentication-flows";
import { getNotificationState } from "./utils/user-account-helper";

import { authToken } from "./composables/user-cookies";
import { loginService, logoutService, playerContactService } from "./services/login-services";
import { UrlHelper } from "@/helpers/url-helper";
import { Loader } from "@sazka/design-system/src/loader/loader";
import { verifyEmailService, playerDetailService } from "./services/account-services";
import useAuthentication from "./composables/use-authentication";
import useAuthenticationFlows, { authenticationStep } from "./composables/use-authentication-flows";
import { dataLayerUserInfo } from "@/data-layer/services/account";
import { dataLayerFirstLogin, dataLayerLogin } from "@/data-layer/services/actions";
import { wecomaRedirectActive } from "@sazka/utils/gdpr/use-wecoma-redirect";
import { authenticationData, authenticationErrors } from "./composables/use-authentication-data";

export const userNotificationState = ref<AccountNotificationType>("None");

export const userAccountData = ref<PlayerDetailModel>(null);
export const isUserLoggedIn = computed(() => !!userAccountData.value);

const externalClientId = ref<string>(null);
export const clientId = computed({
    get() {
        return externalClientId.value || userAccountData.value?.clientId;
    },
    set(newClientId) {
        externalClientId.value = newClientId;
    },
});

const { openLoginModal, openRegistrationModal, closeAccountModal } = useAuthenticationFlows();
const { nextAuthenticationStep } = useAuthentication();

const tryGetUserAccount = computed(() => !!authToken.value);

/* *************************** LOGIN CALLBACK **************************** */
//#region Login callback

let onLoginCallbacks: ((userAccountData: PlayerDetailModel) => void)[] = [];
/**
 * Non blocking callbacks, when user is logged in, these functions will be resolving asynchronously after.
 * @param callbackFunction will be called, when user logged in.
 * @returns
 */
const addOnLoginCallback = (callbackFunction: (userAccountData: PlayerDetailModel) => void) => {
    if (isUserLoggedIn.value) {
        callbackFunction(userAccountData.value);
        return;
    }

    onLoginCallbacks.push(callbackFunction);
};

const resolveLoginCallbacks = () => {
    for (const callback of onLoginCallbacks) {
        callback(userAccountData.value);
    }

    onLoginCallbacks = [];
};

//#endregion

/* *********************** LOGIN WAITERS ****************************** */
//#region Login waiters

let loggedInWaiters: ((userAccountData: Ref<PlayerDetailModel>) => void)[] = [];

/**
 * Blocking functions. This collected waiters and resolve then when user data are avalable.
 * @returns Promise returs player data.
 */
const waitForUserAccount = async (): Promise<Ref<PlayerDetailModel>> => {
    return new Promise((res) => {
        if (!tryGetUserAccount.value || isUserLoggedIn.value) {
            res(userAccountData);
            return;
        }

        loggedInWaiters.push(res);
    });
};

const resolveLoggedInWaiters = () => {
    for (const res of loggedInWaiters) {
        res(userAccountData);
    }

    loggedInWaiters = [];
};
//#endregion

/* ****************************** PLAYER DATA FUNCTIONS ********************** */
//#region Player data function

const refreshPlayerData = async () => {
    try {
        userAccountData.value = await playerDetailService();
        userNotificationState.value = getNotificationState(userAccountData.value);
    } catch (ex: unknown) {
        await logoutService();
    }
};

const init = async () => {
    const action = UrlHelper.getQueryString("akce");
    const username = UrlHelper.getQueryString("username");
    const token = UrlHelper.getQueryString("token");

    if (username && token) {
        await verifyEmailService(username, token);
    }

    if (action == "login") {
        if (tryGetUserAccount.value) {
            await logoutService();
            resolveLoggedInWaiters();
        }

        if (username) {
            authenticationData.userName = username;
            openLoginModal();
            await nextAuthenticationStep();
        } else {
            openLoginModal();
        }
    } else if (action == "registrace") {
        if (tryGetUserAccount.value) {
            await logoutService();
            resolveLoggedInWaiters();
        }

        openRegistrationModal();
    } else if (tryGetUserAccount.value) {
        await refreshPlayerData();
        resolveLoggedInWaiters();

        if (isUserLoggedIn.value) {
            resolveLoginCallbacks();
            dataLayerUserInfo(userAccountData.value.cardId, clientId.value);
        } else if (wecomaRedirectActive) {
            openLoginModal();
        }
    }

    if (action || username || token) {
        UrlHelper.clearUrlParams(["akce", "username", "token"]);
    }
};

init();

//#endregion

/* ***************************** LOGIN FUNTIONS ************************** */
//#region  Login functions
watch(isUserLoggedIn, (newIsLoggedIn) => {
    if (newIsLoggedIn) {
        document.body.classList.add("authenticated");
        document.body.classList.remove("unauthenticated", "session-unknown");
    } else {
        document.body.classList.add("unauthenticated");
        document.body.classList.remove("authenticated", "session-unknown");
    }
});

const loginUser = async (): Promise<boolean> => {
    Loader.show();

    try {
        await loginService(authenticationData.userName, authenticationData.password);
        await refreshPlayerData();

        await playerContactService(userAccountData.value);

        resolveLoginCallbacks();

        closeAccountModal();
    } catch (ex: unknown) {
        authenticationErrors.passwordErrorMessage = ex as string;
        return false;
    } finally {
        Loader.hide();
    }

    if (authenticationStep.value == "LoginWelcome") {
        dataLayerFirstLogin();
    } else {
        dataLayerLogin();
    }

    return true;
};

//#endregion

export default function useUserAccount() {
    return {
        userAccountData,

        addOnLoginCallback,
        waitForUserAccount,
        refreshPlayerData,
        loginUser,
    };
}
